import React, { FunctionComponent, useEffect, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAtom } from 'jotai';
import { useTranslation } from 'react-i18next'
// import useMediaQuery from '@mui/material/useMediaQuery';

// import { check_subdomain } from '../../lib/server_helper';
import { fetch_all, fetch_one } from "../../../lib/v31lib";
import { loginAtom } from '../../../lib/auth';
import { currentPracticeAtom } from '../../../lib/practice';

import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CompressIcon from '@mui/icons-material/Compress';
import Delete from '@mui/icons-material/Delete';
import ExpandIcon from '@mui/icons-material/Expand';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import {
  Alert,
  AppBar,
  Box,
  CircularProgress,
  Divider,
  Grid,
  ListSubheader,
  MenuItem,
  Paper,
  Tab,
  Tabs,
} from '@mui/material';

import {
  Button,
  DatePicker,
  Page,
  Select,
  Switcher,
  TextInput,
  Typography
} from '../../../components/v2/styled';
import { SnackSaved } from '../../../components/v2/snacks/Saved';

import { Questionnaire } from '../../../models/Questionnaire';

import HelpersLanguage from '../../../actions/helpers/language';

const { DateTime } = require("luxon");

type Props = {}

export const CoachQuestionnaireEdit: FunctionComponent<Props> = ({}) => {
  const [login, setLogin] = useAtom(loginAtom);
  const [currentPractice, setCurrentPractice] = useAtom(currentPracticeAtom);
  const theme = useTheme();
  const navigate = useNavigate();
  const {t, i18n} = useTranslation(['translations']);
  const params = useParams();
  const queryClient = useQueryClient();
  // const { state } = useLocation();
  
  const [objectId, setObjectId] = useState<string | undefined>(params.id);
  const [savedOpen, setSavedOpen] = useState<boolean>(false);
  const [mainObject, setMainObject] = useState<Questionnaire>({
    name: ''
  });
  const [currentLanguageId, setCurrentLanguageId] = useState<number>(1);
  const [currentLanguage, setCurrentLanguage] = useState<string>('nl');
  const [clapped, setClapped] = useState<number[]>([]);

  const [errors, setErrors] = useState<{[z:string]: boolean}>({});

  const {
    data: object,
    isLoading: objectLoading,
    isError: objectError,
    isSuccess: objectSuccess,
    refetch: objectRefetch,
  } = useQuery({
    queryKey: ["questionnaire", (login?.practice_id || currentPractice?.id), objectId],
    queryFn: () =>
      fetch_one<Questionnaire>(
        `/${i18n.resolvedLanguage || 'nl'}/v3/objects/fetch_all`,
        {
          object: 'questionnaire',
          id: objectId,
          fields: [
            'id', 'name', 'extra_data', 'questionnaire_results|completed|count', 'content_type'
          ],
          practice_id: (login?.practice_id || currentPractice?.id)
        },
        login
      ),
    // select: (d) => {
    //   return {
    //     ...d,
    //     birth_date: DateTime.fromISO(d.birth_date).toFormat("dd/LL/y")
    //   };
    // },
    enabled: !!(login?.practice_id || currentPractice?.id) && parseInt(objectId || '', 10) > 0,
  });







  const mutationSave = useMutation({
    mutationFn: (pars: {
      formData: any;
      keepOpen?: boolean
    }) => {
      return fetch_one<Questionnaire>(
        `/${i18n.resolvedLanguage || 'nl'}/v3/objects/save`,
        pars.formData,
        login
      );
    },
    onMutate: (pars: {
      formData: any;
    }) => {
      return true;
    },
    onError: (data, variables, context) => {
      return true;
    },
    onSuccess: (data, variables, context) => {
      setSavedOpen(true);
      queryClient.invalidateQueries({ queryKey: ["coach", "questionnaires", (login?.practice_id || currentPractice?.id)] });
      if (!variables.keepOpen) navigate("/questionnaires");
      if (!!variables.keepOpen && objectId === 'new') navigate(`/questionnaire/${data.id}`);
      if (!!variables.keepOpen && objectId !== 'new') objectRefetch();
    },
  });








  const notAQuiz = (mo:Questionnaire, q:any) => {
    return (mo.content_type !== 'quiz' || (mo.content_type === 'quiz' && !q.is_quiz));
  }

  const saveObject = (keep_open: boolean) => {
    mutationSave.mutate({
      formData: {
        object: 'questionnaire',
        handler_id: login?.id,
        practice_id:  (login?.practice_id || currentPractice?.id),
        fields: ['id'],
        id: mainObject?.id,
        ob: {
          practice_id: (login?.practice_id || currentPractice?.id),
          ...mainObject
        }
      },
      keepOpen: keep_open
    });
  };

  let page_title;
  if (typeof((object || {}).id) === 'undefined') {
    page_title = t("general.titles.add", "Nieuwe") + " " + t("coach.models.questionnaires.singular", "Vragenlijst");
  } else {
    page_title = t("general.titles.edit", "Wijzig") + " " + (object || {}).name;
  }

  useEffect(() => {
    if (params.id !== 'new') {
      setObjectId(params.id);
      // objectRefetch();
    }
  }, [params.id]);
  useEffect(() => {
    if (!!objectSuccess) setMainObject(object);
  }, [object]);
  useEffect(() => {
    let err:any = {};
    if (!mainObject.name) err.name = true;
    if ((mainObject.extra_data?.questions || []).length < 1) err.questions = true;

    setErrors(err);
  }, [mainObject]);

  return <Page
    title={page_title}
    titleCrumbs={[
      {
        title: t("coach.menu.questionnaires", "Vragenlijsten"),
        path: "/questionnaires"
      }
    ]}
  >
    <Paper sx={{padding: 2}}>
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <Typography variant="h5">{t("coach.submenu.sections.basic")}</Typography>
          <Divider />
        </Grid>
        <Grid item xs={12}>
          <Alert severity="info">{t("coach.submenu.questionnaires.link_appointment_type_help")}</Alert>
        </Grid>
        {(mainObject.content_type || 'normal') === 'review' && <Grid item xs={12}>
          <Alert severity="warning">{t("coach.submenu.questionnaires.review_questions_help")}</Alert>
        </Grid>}
        <Grid item xs={12} md={6}>
          <TextInput
            id="name"
            caption={t("coach.models.questionnaires.name")}
            starred
            placeholder={t("coach.models.questionnaires.placeholders.name", "Vul hier de naam in")}
            value={mainObject.name || ''}
            onChange={(e) => {
              setMainObject({
                ...mainObject,
                name: e
              });
            }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Select
            fullWidth
            caption={t("coach.models.questionnaires.content_type")}
            displayEmpty
            id="gender"
            value={mainObject.content_type || 'normal'}
            setValue={(v) => {
              setMainObject({
                ...mainObject,
                content_type: v.toString()
              });
            }}
          >
            <MenuItem value="normal">{t("coach.models.questionnaires.content_types.normal")}</MenuItem>
            <MenuItem value="review">{t("coach.models.questionnaires.content_types.review")}</MenuItem>
            <MenuItem value="quiz">{t("coach.models.questionnaires.content_types.quiz")}</MenuItem>
          </Select>
        </Grid>
        <Grid item xs={12} sx={{borderLeft: "4px solid rgba(0, 0, 0, 0.54)", marginTop: 1}}>
          <AppBar position="static" sx={{backgroundColor: theme.palette.primary.contrastText}}>
            <Tabs value={currentLanguageId} onChange={(ev, i) => {
              setCurrentLanguageId(i);
              setCurrentLanguage(HelpersLanguage.mapping_to_iso(i));
              // setFlickerEditor(true);
              // setTimeout(() => {setFlickerEditor(false);}, 200);
            }}>
              {HelpersLanguage.available_locales().map((locale, i) => {
                return (<Tab value={HelpersLanguage.mapping_to_id(locale)} label={locale.toUpperCase()} id={`locale_tab_${locale}`} />)
              })}
            </Tabs>
          </AppBar>
        </Grid>
        <Grid item xs={12} sx={{borderLeft: "4px solid rgba(0, 0, 0, 0.54)", marginTop: 1}}>
          <TextInput
            id="public_name"
            caption={t("general.questionnaires.fields.public_name")}
            value={(mainObject?.extra_data?.public_name || {})[currentLanguage] || ''}
            onChange={(e) => {
              setMainObject({
                ...mainObject,
                extra_data: {
                  ...(mainObject.extra_data || {}),
                  questions: [...(mainObject.extra_data?.questions || [])],
                  public_name: {
                    ...(mainObject.extra_data?.public_name || {}),
                    [currentLanguage]: e
                  }
                }
              });
            }}
          />
        </Grid>
        <Grid item xs={12} sx={{borderLeft: "4px solid rgba(0, 0, 0, 0.54)", marginTop: 1}}>
          <TextInput
            id="intro"
            multiline
            caption={t("general.questionnaires.fields.intro")}
            value={(mainObject?.extra_data?.intro || {})[currentLanguage] || ''}
            onChange={(e) => {
              setMainObject({
                ...mainObject,
                extra_data: {
                  ...(mainObject.extra_data || {}),
                  questions: [...(mainObject.extra_data?.questions || [])],
                  intro: {
                    ...(mainObject.extra_data?.intro || {}),
                    [currentLanguage]: e
                  }
                }
              });
            }}
          />
        </Grid>
        <Grid item xs={12} sx={{marginTop: 5}}>
          <Typography variant="h5">{t("coach.submenu.sections.questions")}</Typography>
          <Divider />
        </Grid>
        {(mainObject.extra_data?.questions || []).map((question, i) => <Grid item xs={12}>
          <Box sx={{
            width: '100%',
            padding: 2,
            paddingTop: 6,
            position: 'relative',
            border: '1px solid transparent',
            borderColor: theme.palette.primary.main,
            marginTop: 1,
            marginBottom: 1,
            borderRadius: 1,
            ...(!!question.section ? {
              backgroundImage: `linear-gradient(to right, transparent, transparent, ${theme.palette.primary.main})`
            } : {})
          }}>
            <Box sx={{
              position: 'absolute',
              top: 8,
              left: 8,
            }}>
              <Button
                size="small"
                disabled={(i < 1) || ((mainObject.questionnaire_results_completed_count || 0) > 0)}
                startIcon={<ExpandLessIcon />}
                id="move_up"
                onClick={() => {
                  let newQuestions = [...(mainObject.extra_data?.questions || [])];

                  let temp = newQuestions[i - 1];
                  newQuestions[i - 1] = newQuestions[i];
                  newQuestions[i] = temp;

                  setMainObject({
                    ...mainObject,
                    extra_data: {
                      ...(mainObject.extra_data || {}),
                      questions: newQuestions
                    }
                  });
                }}
                label={t("general.actions.move_up")}
                sx={{
                  marginTop: 0,
                  paddingTop: 0.25
                }}
              />
              <Button
                size="small"
                disabled={(i >= ((mainObject.extra_data?.questions || []).length - 1)) || ((mainObject.questionnaire_results_completed_count || 0) > 0)}
                startIcon={<ExpandMoreIcon />}
                id="move_down"
                onClick={() => {
                  let newQuestions = [...(mainObject.extra_data?.questions || [])];

                  let temp = newQuestions[i + 1];
                  newQuestions[i + 1] = newQuestions[i];
                  newQuestions[i] = temp;

                  setMainObject({
                    ...mainObject,
                    extra_data: {
                      ...(mainObject.extra_data || {}),
                      questions: newQuestions
                    }
                  });
                }}
                label={t("general.actions.move_down")}
                sx={{
                  marginTop: 0,
                  marginLeft: 1,
                  paddingTop: 0.25
                }}
              />
            </Box>
            <Box sx={{
              position: 'absolute',
              top: 8,
              left: '40%',
            }}>
              {clapped.indexOf(i) > -1 && <Button
                size="small"
                startIcon={<ExpandIcon />}
                id="clap_out"
                onClick={() => {
                  setClapped(clapped.filter(fff => fff !== i));
                }}
                label={t("general.actions.expand")}
                sx={{
                  marginTop: 0,
                  paddingTop: 0.25
                }}
              />}
              {clapped.indexOf(i) < 0 && <Button
                size="small"
                startIcon={<CompressIcon />}
                id="clap_in"
                onClick={() => {
                  setClapped([...clapped, i]);
                }}
                label={t("general.actions.unexpand")}
                sx={{
                  marginTop: 0,
                  paddingTop: 0.25
                }}
              />}
            </Box>
            {clapped.indexOf(i) > -1 && <Button
              size="small"
              secondary
              contained
              destroyBtn
              startIcon={<Delete />}
              id="destroy_q"
              onClick={() => {
                let newQuestions:any[] = [];
                (mainObject.extra_data?.questions || []).forEach((qq, ii) => {
                  if (ii === i) {
                    
                  } else {
                    newQuestions.push(qq);
                  }
                });

                setMainObject({
                  ...mainObject,
                  extra_data: {
                    ...(mainObject.extra_data || {}),
                    questions: newQuestions
                  }
                });
              }}
              label={t("general.actions.destroy")}
              sx={{
                marginTop: 0,
                position: 'absolute',
                top: 8,
                right: 8,
                paddingTop: 0.25
              }}
            />}
            <Divider sx={{
              position: 'absolute',
              left: 0,
              top: 42,
              width: '100%'
            }} />

            <Grid container spacing={1}>
              {clapped.indexOf(i) > -1 && <Grid item xs={12} sx={{borderLeft: "4px solid rgba(0, 0, 0, 0.54)", marginTop: 1}}>
                <AppBar position="static" sx={{backgroundColor: theme.palette.primary.contrastText}}>
                  <Tabs value={currentLanguageId} onChange={(ev, i) => {
                    setCurrentLanguageId(i);
                    setCurrentLanguage(HelpersLanguage.mapping_to_iso(i));
                    // setFlickerEditor(true);
                    // setTimeout(() => {setFlickerEditor(false);}, 200);
                  }}>
                    {HelpersLanguage.available_locales().map((locale, i) => {
                      return (<Tab value={HelpersLanguage.mapping_to_id(locale)} label={locale.toUpperCase()} id={`locale_tab_${locale}`} />)
                    })}
                  </Tabs>
                </AppBar>
              </Grid>}
              {!!question.section && <>
                <Grid item xs={12} sx={{borderLeft: "4px solid rgba(0, 0, 0, 0.54)"}}>
                  <TextInput
                    id="section_title"
                    caption={t("general.questionnaires.fields.section_title")}
                    value={(question.section_title || {})[currentLanguage] || ''}
                    onChange={(e) => {
                      let newQuestions:any[] = [];
                      (mainObject.extra_data?.questions || []).forEach((qq, ii) => {
                        if (ii === i) {
                          let newQ = {...qq};
                          newQ.section_title = {
                            ...(newQ.section_title || {}),
                            [currentLanguage]: e
                          }
                          newQuestions.push(newQ);
                        } else {
                          newQuestions.push(qq);
                        }
                      });

                      setMainObject({
                        ...mainObject,
                        extra_data: {
                          ...(mainObject.extra_data || {}),
                          questions: newQuestions
                        }
                      });
                    }}
                  />
                </Grid>
                {clapped.indexOf(i) > -1 && <Grid item xs={12} sx={{borderLeft: "4px solid rgba(0, 0, 0, 0.54)"}}>
                  <TextInput
                    id="section_content"
                    multiline
                    caption={t("general.questionnaires.fields.section_content")}
                    value={(question.section_content || {})[currentLanguage] || ''}
                    onChange={(e) => {
                      let newQuestions:any[] = [];
                      (mainObject.extra_data?.questions || []).forEach((qq, ii) => {
                        if (ii === i) {
                          let newQ = {...qq};
                          newQ.section_content = {
                            ...(newQ.section_content || {}),
                            [currentLanguage]: e
                          }
                          newQuestions.push(newQ);
                        } else {
                          newQuestions.push(qq);
                        }
                      });

                      setMainObject({
                        ...mainObject,
                        extra_data: {
                          ...(mainObject.extra_data || {}),
                          questions: newQuestions
                        }
                      });
                    }}
                  />
                </Grid>}
              </>}
              {!question.section && <>
                <Grid item xs={12} sx={{borderLeft: "4px solid rgba(0, 0, 0, 0.54)"}}>
                  <TextInput
                    id="question"
                    caption={t("general.questionnaires.fields.question")}
                    starred
                    value={(question.question || {})[currentLanguage] || ''}
                    onChange={(e) => {
                      let newQuestions:any[] = [];
                      (mainObject.extra_data?.questions || []).forEach((qq, ii) => {
                        if (ii === i) {
                          let newQ = {...qq};
                          newQ.question = {
                            ...(newQ.question || {}),
                            [currentLanguage]: e
                          }
                          newQuestions.push(newQ);
                        } else {
                          newQuestions.push(qq);
                        }
                      });

                      setMainObject({
                        ...mainObject,
                        extra_data: {
                          ...(mainObject.extra_data || {}),
                          questions: newQuestions
                        }
                      });
                    }}
                  />
                </Grid>
                {clapped.indexOf(i) > -1 && <>
                  <Grid item xs={12} sx={{borderLeft: "4px solid rgba(0, 0, 0, 0.54)"}}>
                    <TextInput
                      id="help"
                      caption={t("general.questionnaires.fields.help")}
                      multiline
                      value={(question.help || {})[currentLanguage] || ''}
                      onChange={(e) => {
                        let newQuestions:any[] = [];
                        (mainObject.extra_data?.questions || []).forEach((qq, ii) => {
                          if (ii === i) {
                            let newQ = {...qq};
                            newQ.help = {
                              ...(newQ.help || {}),
                              [currentLanguage]: e
                            }
                            newQuestions.push(newQ);
                          } else {
                            newQuestions.push(qq);
                          }
                        });

                        setMainObject({
                          ...mainObject,
                          extra_data: {
                            ...(mainObject.extra_data || {}),
                            questions: newQuestions
                          }
                        });
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Select
                      fullWidth
                      caption={t("general.questionnaires.fields.question_type")}
                      starred
                      backend
                      displayEmpty
                      id="type"
                      value={question.type?.toString() || 'open'}
                      setValue={(v) => {
                        let newQuestions:any[] = [];
                        (mainObject.extra_data?.questions || []).forEach((qq, ii) => {
                          if (ii === i) {
                            let newQ = {...qq};
                            newQ.type = (v?.toString() || 'open');
                            newQ.is_quiz = (mainObject.content_type === 'quiz' && !(newQ.type === "open_long" || newQ.type === "nps" || newQ.type === "existing"));
                            newQ.min = undefined;
                            newQ.max = undefined;
                            newQ.options = undefined;
                            newQuestions.push(newQ);
                            console.log(newQ.is_quiz);
                          } else {
                            newQuestions.push(qq);
                          }
                        });

                        setMainObject({
                          ...mainObject,
                          extra_data: {
                            ...(mainObject.extra_data || {}),
                            questions: newQuestions
                          }
                        });
                      }}
                    >
                      <MenuItem value="open">{t("general.questionnaires.fields.question_types.open", "Open vraag (kort)")}</MenuItem>
                      {mainObject.content_type !== 'quiz' && <MenuItem value="open_long">{t("general.questionnaires.fields.question_types.open_long", "Open vraag (lang)")}</MenuItem>}
                      {mainObject.content_type === 'quiz' && <MenuItem value="open_long">{t("general.questionnaires.fields.question_types.open_long_quiz_warning", "Open vraag (lang)")}</MenuItem>}
                      <MenuItem value="single_radio">{t("general.questionnaires.fields.question_types.single_radio", "Enkele keuze (radio buttons)")}</MenuItem>
                      <MenuItem value="single_select">{t("general.questionnaires.fields.question_types.single_select", "Enkele keuze (select)")}</MenuItem>
                      <MenuItem value="multi">{t("general.questionnaires.fields.question_types.multi", "Meerkeuze (checkboxes)")}</MenuItem>
                      {mainObject.content_type !== 'quiz' && <MenuItem value="nps">{t("general.questionnaires.fields.question_types.nps", "NPS")}</MenuItem>}
                      {mainObject.content_type === 'quiz' && <MenuItem value="nps">{t("general.questionnaires.fields.question_types.nps_quiz_warning", "NPS")}</MenuItem>}
                      {mainObject.content_type !== 'quiz' && <MenuItem value="existing">{t("general.questionnaires.fields.question_types.existing", "Bestaande vraag")}</MenuItem>}
                      {mainObject.content_type === 'quiz' && <MenuItem value="existing">{t("general.questionnaires.fields.question_types.existing_quiz_warning", "Bestaande vraag")}</MenuItem>}
                    </Select>
                  </Grid>
                  {mainObject.content_type === 'quiz' && <Grid item xs={6}>
                    <Switcher
                      id="is_quiz"
                      disabled={(question.type === "open_long" || question.type === "nps" || question.type === "existing")}
                      caption={t("general.questionnaires.fields.is_quiz_question")}
                      value={!!question?.is_quiz}
                      onChange={(c) => {
                        let newQuestions:any[] = [];
                        (mainObject.extra_data?.questions || []).forEach((qq, ii) => {
                          if (ii === i) {
                            let newQ = {...qq};
                            newQ.is_quiz = !!c;
                            newQuestions.push(newQ);
                          } else {
                            newQuestions.push(qq);
                          }
                        });

                        setMainObject({
                          ...mainObject,
                          extra_data: {
                            ...(mainObject.extra_data || {}),
                            questions: newQuestions
                          }
                        });
                      }}
                    />
                  </Grid>}
                  <Grid item xs={mainObject.content_type === 'quiz' ? 4 : 6}>
                    {notAQuiz(mainObject, question) && <Switcher
                      id="required"
                      // disabled={mainObject.content_type === 'quiz'}
                      caption={t("general.questionnaires.fields.required")}
                      value={!!question?.required}
                      onChange={(c) => {
                        let newQuestions:any[] = [];
                        (mainObject.extra_data?.questions || []).forEach((qq, ii) => {
                          if (ii === i) {
                            let newQ = {...qq};
                            newQ.required = !!c;
                            newQuestions.push(newQ);
                          } else {
                            newQuestions.push(qq);
                          }
                        });

                        setMainObject({
                          ...mainObject,
                          extra_data: {
                            ...(mainObject.extra_data || {}),
                            questions: newQuestions
                          }
                        });
                      }}
                    />}
                  </Grid>
                  {notAQuiz(mainObject, question) && ['open', 'open_long', 'multi', 'nps'].indexOf(question.type || 'open') > -1 && <Grid item xs={mainObject.content_type === 'quiz' ? 4 : 6}>
                    <TextInput
                      id="min"
                      caption={t(`general.questionnaires.fields.option.min_${question.type || 'open'}`)}
                      value={question.min?.toString() || ''}
                      onChange={(e) => {
                        let newQuestions:any[] = [];
                        (mainObject.extra_data?.questions || []).forEach((qq, ii) => {
                          if (ii === i) {
                            let newQ = {...qq};
                            newQ.min = !!e ? parseInt(e, 10) : undefined
                            newQuestions.push(newQ);
                          } else {
                            newQuestions.push(qq);
                          }
                        });

                        setMainObject({
                          ...mainObject,
                          extra_data: {
                            ...(mainObject.extra_data || {}),
                            questions: newQuestions
                          }
                        });
                      }}
                    />
                  </Grid>}
                  {notAQuiz(mainObject, question) && ['open', 'open_long', 'multi', 'nps'].indexOf(question.type || 'open') > -1 && <Grid item xs={mainObject.content_type === 'quiz' ? 4 : 6}>
                    <TextInput
                      id="max"
                      caption={t(`general.questionnaires.fields.option.max_${question.type || 'open'}`)}
                      value={question.max?.toString() || ''}
                      onChange={(e) => {
                        let newQuestions:any[] = [];
                        (mainObject.extra_data?.questions || []).forEach((qq, ii) => {
                          if (ii === i) {
                            let newQ = {...qq};
                            newQ.max = !!e ? parseInt(e, 10) : undefined
                            newQuestions.push(newQ);
                          } else {
                            newQuestions.push(qq);
                          }
                        });

                        setMainObject({
                          ...mainObject,
                          extra_data: {
                            ...(mainObject.extra_data || {}),
                            questions: newQuestions
                          }
                        });
                      }}
                    />
                  </Grid>}
                  {mainObject.content_type === 'quiz' && !!question.is_quiz && ['open'].indexOf(question.type || 'open') > -1 && <Grid item xs={8}>
                    <TextInput
                      id="correct_open"
                      caption={t(`general.questionnaires.fields.option.correct_open`)}
                      value={question.correct_open || ''}
                      onChange={(e) => {
                        let newQuestions:any[] = [];
                        (mainObject.extra_data?.questions || []).forEach((qq, ii) => {
                          if (ii === i) {
                            let newQ = {...qq};
                            newQ.correct_open = e
                            newQuestions.push(newQ);
                          } else {
                            newQuestions.push(qq);
                          }
                        });

                        setMainObject({
                          ...mainObject,
                          extra_data: {
                            ...(mainObject.extra_data || {}),
                            questions: newQuestions
                          }
                        });
                      }}
                    />
                  </Grid>}
                  {['multi', 'single_radio', 'single_select'].indexOf(question.type || 'open') > -1 && <Grid item xs={12} sx={{borderLeft: "4px solid rgba(0, 0, 0, 0.54)"}}>
                    <TextInput
                      id="question"
                      caption={t("general.questionnaires.fields.option.options")}
                      multiline
                      starred
                      sx={{
                        ...(!((question.options || {})[currentLanguage] || '')) ? {
                          borderColor: 'red'
                        } : {}
                      }}
                      value={(question.options || {})[currentLanguage] || ''}
                      onChange={(e) => {
                        let newQuestions:any[] = [];
                        (mainObject.extra_data?.questions || []).forEach((qq, ii) => {
                          if (ii === i) {
                            let newQ = {...qq};
                            newQ.options = {
                              ...(newQ.options || {}),
                              [currentLanguage]: e
                            }
                            newQuestions.push(newQ);
                          } else {
                            newQuestions.push(qq);
                          }
                        });

                        setMainObject({
                          ...mainObject,
                          extra_data: {
                            ...(mainObject.extra_data || {}),
                            questions: newQuestions
                          }
                        });
                      }}
                    />
                  </Grid>}
                  {mainObject.content_type === 'quiz' && !!question.is_quiz && ['multi', 'single_radio', 'single_select'].indexOf(question.type || 'open') > -1 && <Grid item xs={8} sx={{borderLeft: "4px solid rgba(0, 0, 0, 0.54)"}}>
                    <TextInput
                      id="correct_options"
                      caption={t("general.questionnaires.fields.option.correct_options")}
                      multiline
                      value={(question.correct_options || {})[currentLanguage] || ''}
                      onChange={(e) => {
                        let newQuestions:any[] = [];
                        (mainObject.extra_data?.questions || []).forEach((qq, ii) => {
                          if (ii === i) {
                            let newQ = {...qq};
                            newQ.correct_options = {
                              ...(newQ.correct_options || {}),
                              [currentLanguage]: e
                            }
                            newQuestions.push(newQ);
                          } else {
                            newQuestions.push(qq);
                          }
                        });

                        setMainObject({
                          ...mainObject,
                          extra_data: {
                            ...(mainObject.extra_data || {}),
                            questions: newQuestions
                          }
                        });
                      }}
                    />
                  </Grid>}
                  {['nps'].indexOf(question.type || 'open') > -1 && <Grid item xs={12}>
                    <Select
                      fullWidth
                      caption={t("general.questionnaires.fields.option.nps")}
                      backend
                      displayEmpty
                      id="option_nps"
                      value={question.is_nps?.toString() || 'no'}
                      setValue={(v) => {
                        let newQuestions:any[] = [];
                        (mainObject.extra_data?.questions || []).forEach((qq, ii) => {
                          if (ii === i) {
                            let newQ = {...qq};
                            newQ.is_nps = v.toString();
                            newQuestions.push(newQ);
                          } else {
                            newQuestions.push(qq);
                          }
                        });

                        setMainObject({
                          ...mainObject,
                          extra_data: {
                            ...(mainObject.extra_data || {}),
                            questions: newQuestions
                          }
                        });
                      }}
                    >
                      <MenuItem value="no">{t("general.questionnaires.fields.is_nps.no", "Geen NPS vraag (meet score)")}</MenuItem>
                      <MenuItem value="nps_eu">{t("general.questionnaires.fields.is_nps.nps_eu", "NPS vraag (EU-calculatie, 8-9-10 promotor)")}</MenuItem>
                      <MenuItem value="nps_intl">{t("general.questionnaires.fields.is_nps.nps_intl", "NPS vraag (internationale calculatie, 9-10 promotor)")}</MenuItem>
                    </Select>
                  </Grid>}
                  {['existing'].indexOf(question.type || 'open') > -1 && <Grid item xs={12}>
                    <Select
                      fullWidth
                      caption={t("general.questionnaires.fields.existing_question_id")}
                      backend
                      displayEmpty
                      id="existing_question_id"
                      value={question.existing_question_id || ''}
                      setValue={(v) => {
                        let newQuestions:any[] = [];
                        (mainObject.extra_data?.questions || []).forEach((qq, ii) => {
                          if (ii === i) {
                            let newQ = {...qq};
                            newQ.existing_question_id = !!v ? v.toString() : undefined;
                            newQuestions.push(newQ);
                          } else {
                            newQuestions.push(qq);
                          }
                        });

                        setMainObject({
                          ...mainObject,
                          extra_data: {
                            ...(mainObject.extra_data || {}),
                            questions: newQuestions
                          }
                        });
                      }}
                    >
                      <MenuItem value="">--- ---</MenuItem>
                      
                      <ListSubheader>{t("general.questionnaires.existing.profile_name")}</ListSubheader>
                      <MenuItem value="profile|first_name">{t("general.questionnaires.existing.profile.first_name")}</MenuItem>
                      <MenuItem value="profile|last_name">{t("general.questionnaires.existing.profile.last_name")}</MenuItem>

                      <ListSubheader sx={{marginTop: 1}}>{t("general.questionnaires.existing.profile_contact_name")}</ListSubheader>
                      <MenuItem value="profile|phone_mobile">{t("general.questionnaires.existing.profile.phone_mobile")}</MenuItem>
                      <MenuItem value="profile|phone_fixed">{t("general.questionnaires.existing.profile.phone_fixed")}</MenuItem>

                      <ListSubheader sx={{marginTop: 1}}>{t("general.questionnaires.existing.intake_name")}</ListSubheader>
                      <MenuItem value="intake|practician">{t("general.questionnaires.existing.intake.practician")}</MenuItem>
                      <MenuItem value="intake|insurance">{t("general.questionnaires.existing.intake.insurance")}</MenuItem>
                      <MenuItem value="intake|extra_insurance">{t("general.questionnaires.existing.intake.extra_insurance")}</MenuItem>
                      <MenuItem value="intake|convention">{t("general.questionnaires.existing.intake.convention")}</MenuItem>
                      <MenuItem value="intake|height">{t("general.questionnaires.existing.intake.height")}</MenuItem>
                      <MenuItem value="intake|weight">{t("general.questionnaires.existing.intake.weight")}</MenuItem>
                      <MenuItem value="intake|weight_progress">{t("general.questionnaires.existing.intake.weight_progress")}</MenuItem>
                      <MenuItem value="intake|civil_state">{t("general.questionnaires.existing.intake.civil_state")}</MenuItem>
                      <MenuItem value="intake|children">{t("general.questionnaires.existing.intake.children")}</MenuItem>
                      <MenuItem value="intake|diagnoses">{t("general.questionnaires.existing.intake.diagnoses")}</MenuItem>
                      <MenuItem value="intake|complaints">{t("general.questionnaires.existing.intake.complaints")}</MenuItem>
                      <MenuItem value="intake|medication">{t("general.questionnaires.existing.intake.medication")}</MenuItem>
                      <MenuItem value="intake|allergies">{t("general.questionnaires.existing.intake.allergies")}</MenuItem>
                      <MenuItem value="intake|history">{t("general.questionnaires.existing.intake.history")}</MenuItem>
                      <MenuItem value="intake|situation_home">{t("general.questionnaires.existing.intake.situation_home")}</MenuItem>
                      <MenuItem value="intake|situation_work">{t("general.questionnaires.existing.intake.situation_work")}</MenuItem>
                      <MenuItem value="intake|hobbies">{t("general.questionnaires.existing.intake.hobbies")}</MenuItem>
                      <MenuItem value="intake|sports">{t("general.questionnaires.existing.intake.sports")}</MenuItem>
                      <MenuItem value="intake|is_smoker">{t("general.questionnaires.existing.intake.is_smoker")}</MenuItem>
                      <MenuItem value="intake|comment_smoker">{t("general.questionnaires.existing.intake.comment_smoker")}</MenuItem>
                      <MenuItem value="intake|is_alcohol">{t("general.questionnaires.existing.intake.is_alcohol")}</MenuItem>
                      <MenuItem value="intake|comment_alcohol">{t("general.questionnaires.existing.intake.comment_alcohol")}</MenuItem>
                      <MenuItem value="intake|hours_sit">{t("general.questionnaires.existing.intake.hours_sit")}</MenuItem>
                      <MenuItem value="intake|is_sleep_okay">{t("general.questionnaires.existing.intake.is_sleep_okay")}</MenuItem>
                      <MenuItem value="intake|hours_sleep">{t("general.questionnaires.existing.intake.hours_sleep")}</MenuItem>
                      <MenuItem value="intake|relaxation">{t("general.questionnaires.existing.intake.relaxation")}</MenuItem>
                      <MenuItem value="intake|hours_screen">{t("general.questionnaires.existing.intake.hours_screen")}</MenuItem>
                      <MenuItem value="intake|stress_score">{t("general.questionnaires.existing.intake.stress_score")}</MenuItem>
                      <MenuItem value="intake|food_pattern">{t("general.questionnaires.existing.intake.food_pattern")}</MenuItem>
                      <MenuItem value="intake|consult_initiative">{t("general.questionnaires.existing.intake.consult_initiative")}</MenuItem>
                      <MenuItem value="intake|consult_reason">{t("general.questionnaires.existing.intake.consult_reason")}</MenuItem>
                      <MenuItem value="intake|doctor_note">{t("general.questionnaires.existing.intake.doctor_note")}</MenuItem>
                      <MenuItem value="intake|consult_expectation">{t("general.questionnaires.existing.intake.consult_expectation")}</MenuItem>
                      <MenuItem value="intake|motivation_score">{t("general.questionnaires.existing.intake.motivation_score")}</MenuItem>
                      <MenuItem value="intake|goals">{t("general.questionnaires.existing.intake.goals")}</MenuItem>
                    </Select>
                  </Grid>}
                  {mainObject.content_type === 'quiz' && !!question.is_quiz && <Grid item xs={4}>
                    <TextInput
                      id="min"
                      caption={t(`general.questionnaires.fields.option.quiz_points`)}
                      value={question.quiz_points?.toString() || ''}
                      onChange={(e) => {
                        let newQuestions:any[] = [];
                        (mainObject.extra_data?.questions || []).forEach((qq, ii) => {
                          if (ii === i) {
                            let newQ = {...qq};
                            newQ.quiz_points = !!e ? parseInt(e, 10) : undefined
                            newQuestions.push(newQ);
                          } else {
                            newQuestions.push(qq);
                          }
                        });

                        setMainObject({
                          ...mainObject,
                          extra_data: {
                            ...(mainObject.extra_data || {}),
                            questions: newQuestions
                          }
                        });
                      }}
                    />
                  </Grid>}
                </>}
              </>}
            </Grid>
          </Box>
        </Grid>)}
        <Grid item xs={12}>
          <Box sx={{
            width: '30%',
            padding: 2,
            position: 'relative',
            border: '1px dashed transparent',
            borderColor: theme.palette.primary.main,
            marginTop: 1,
            marginBottom: 1,
            borderRadius: 1,
            textAlign: 'center',
            cursor: 'pointer',
            display: 'inline-block',
            marginRight: '4.9%'
          }} onClick={() => {
            setMainObject({
              ...mainObject,
              extra_data: {
                ...(mainObject.extra_data || {}),
                questions: [
                  ...(mainObject.extra_data?.questions || []),
                  {
                    section: true,
                    section_title: {'nl': ''},
                    section_content: {'nl': ''}
                  }
                ]
              }
            });
          }}>
            <AddCircleOutlineIcon color="primary" sx={{
              fontSize: '3rem'
            }} />
            <Typography sx={{textAlign: 'center', color: theme.palette.primary.main}}>{t("coach.submenu.questionnaires.add_new_section")}</Typography>
          </Box>
          <Box sx={{
            width: '65%',
            padding: 2,
            position: 'relative',
            border: '1px dashed transparent',
            borderColor: theme.palette.primary.main,
            marginTop: 1,
            marginBottom: 1,
            borderRadius: 1,
            textAlign: 'center',
            cursor: 'pointer',
            display: 'inline-block'
          }} onClick={() => {
            setMainObject({
              ...mainObject,
              extra_data: {
                ...(mainObject.extra_data || {}),
                questions: [
                  ...(mainObject.extra_data?.questions || []),
                  {
                    question: {'nl': ''},
                    help: {'nl': ''},
                    type: 'open',
                    required: mainObject.content_type === 'quiz',
                    is_quiz: mainObject.content_type === 'quiz'
                  }
                ]
              }
            });
          }}>
            <AddCircleOutlineIcon color="primary" sx={{
              fontSize: '3rem'
            }} />
            <Typography sx={{textAlign: 'center', color: theme.palette.primary.main}}>{t("coach.submenu.questionnaires.add_new_question")}</Typography>
          </Box>
        </Grid>






        <Grid item xs={12} sx={{
          alignContent: 'baseline',
          textAlign: 'center',
          position: {
              xs: 'relative',
              md: 'sticky'
            },
          bottom: '-24px',
          backgroundColor: 'white',
          zIndex: 1201
        }}>
          <Divider sx={{marginTop: 1, marginBottom: 0}} />

          {!!mutationSave.isPending && <CircularProgress />}
          {!mutationSave.isPending && <Button
            label={t("shared.buttons.save")}
            id='save'
            disabled={Object.keys(errors).length > 0}
            contained
            onClick={(v) => {
              saveObject(false);
            }}
            sx={{marginBottom: 1, marginTop: 1}}
          />}
          {!mutationSave.isPending && <Button
            label={t("shared.buttons.save_keep_open")}
            id='save_opened'
            disabled={Object.keys(errors).length > 0}
            contained
            onClick={(v) => {
              saveObject(true);
            }}
            sx={{marginLeft: 1, marginBottom: 1, marginTop: 1}}
          />}
          {!mutationSave.isPending && <Button
            label={t("shared.buttons.cancel")}
            id='cancel'
            text
            // contained
            onClick={(v) => {
              navigate("/questionnaires");
            }}
            sx={{marginLeft: 1, marginBottom: 1, marginTop: 1}}
          />}
        </Grid>
      </Grid>
    </Paper>
    
    <SnackSaved open={savedOpen} setOpen={setSavedOpen} />
  </Page>;
}
